import { FeaturedWorkoutsHeroPanel } from 'src/component/featured-workouts/featured-workouts-hero-panel'
import { FeaturedWorkoutsRail } from 'src/component/featured-workouts/featured-workouts-rail'
import { useSplitLayout } from 'src/hook/use-split-layout'
import { Logo } from '../../util/logo'
import React from 'react'

/**
 * The frame both `/auth/get-in` steps render inside: free workouts on one side, logo over the
 * step's own content on the other.
 *
 * Shared rather than duplicated because the vertical scale below has to be one scale. The email
 * step and the OTP step held byte-identical copies of this frame, and GH-908 added four
 * height-relative values to it — four more things to keep in sync by hand, on the two pages a
 * visitor sees back to back.
 *
 * **The vertical scale is height-relative, and only at `lg` and up (GH-908).** The sign-in column
 * is `min-h-dvh` and was built from fixed pixels — 48px padding, a 74px logo, two 72px social
 * buttons, a 78px field, a 65px captcha, a 64px submit — which totals ~790px. Tailwind's
 * breakpoints are width-based, so a 1440x740 laptop got the full 790px column: the terms line
 * clipped, the page grew a scrollbar, and because the hero panel is `h-dvh overflow-hidden` it
 * did not grow with it, leaving a dead band under the left half.
 *
 * Every ceiling here resolves to the value it replaces at **900px of viewport height** (1dvh =
 * 9px there), so the 1440x900 design target renders exactly as it did before, and every floor is
 * the smallest size the control is still comfortable at. Between them the column fits without
 * scrolling from ~600px of height upward. Same idiom, and same reason, as the GH-909 paywall fix
 * in `choose-plan.tsx`.
 *
 * Below `lg` nothing is clamped: that layout is the rail stacked over the form, where scrolling
 * is normal and expected, and a phone's own assertions cover it.
 */
export const GetInSplitLayout = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const isSplitLayout = useSplitLayout()

  return (
    <div className="min-h-dvh flex flex-col lg:flex-row bg-[#020000] text-ivory">
      {isSplitLayout ? <FeaturedWorkoutsHeroPanel /> : null}
      <div className="flex-1 lg:w-1/2 min-h-dvh flex flex-col gap-6 p-6 sm:p-12 lg:gap-0 lg:py-[clamp(24px,5.3dvh,48px)]">
        <div className="flex justify-center pt-4 lg:pt-[clamp(8px,1.8dvh,16px)]">
          <div className="h-[74px] w-[286px] lg:h-[clamp(52px,8.2dvh,74px)] [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain">
            <Logo />
          </div>
        </div>
        {/* Phones only — at `lg` and up the workouts are the left column instead. Exactly one of
            the two is ever mounted; see `use-split-layout.ts` for why that is a JS branch. */}
        {isSplitLayout ? null : <FeaturedWorkoutsRail />}
        {children}
      </div>
    </div>
  )
}
